import React, { useState, useEffect, useContext } from "react";
import userPlaceholder from "../../../../assets/images/users/user-vector.png";
import { HexColorPicker } from "react-colorful";
import { IoCheckmarkCircle, IoClose } from "react-icons/io5";
import TimePicker from "react-bootstrap-time-picker";
import { UserContext } from "../../../../storage/contexts/UserContext";
import FadeIn from "react-fade-in";
import FilledButton from "../../../../InterstellrComponents/FilledButton";
import CommonTextField from "../../../../InterstellrComponents/CommonTextField";
import { generateURLOptions } from "../../../../URLOptions";
import axios from "axios";
import {
  GET_COMPANY_DETAILS,
  UPDATE_COMPANY_DETAILS,
  UPLOAD_COMPANY_LOGO,
} from "../../../../Routes";
import EventBus from "../../../../storage/EventBus";

import moment from "moment-timezone";

const CompanyConfigurator = () => {
  const { profile } = useContext(UserContext);
  const [company, setCompany] = useState(null);

  const [selectedTime, setSelectedTime] = useState(0);
  const [serverTime, setServerTime] = useState(0);

  const [companyName, setCompanyName] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyColour, setCompanyColour] = useState("#b32aa9");

  // Company Logo
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyLogoUploading, setCompanyLogoUploading] = useState(false);

  // Success Dialog
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  useEffect(() => {
    console.log("USER PROFILE IS: ", JSON.stringify(profile));

    // Set Company Details
    setCompanyName(profile?.company_id?.company_name ?? "");
    setCompanyPhone(profile?.company_id?.company_phone ?? "");
    setCompanyLogo(profile?.company_id?.company_logo ?? null);
    setCompanyColour(profile?.company_id?.company_primary_color ?? "#b32aa9");
    if (profile?.company_id?.company_notification_time) {
      const utcTime = moment
        .utc(profile.company_id.company_notification_time) // parse as UTC
        .local(); // convert to local time

      const totalSeconds =
        utcTime.hours() * 3600 + utcTime.minutes() * 60 + utcTime.seconds();

      setSelectedTime(totalSeconds);
    }
  }, []);

  const handleTimeChange = (time) => {
    setSelectedTime(time);

    // Pick timezone from profile or fallback
    const tz = profile?.timezone ?? "Australia/Adelaide";

    // Start at today's midnight in user's timezone
    const localMoment = moment.tz(tz).startOf("day").add(time, "seconds");

    // Convert to UTC ISO string
    const isoString = localMoment.utc().toISOString();

    console.log("Picker seconds:", time);
    console.log(
      "Local time in tz:",
      localMoment.format("YYYY-MM-DD HH:mm:ss Z"),
    );
    console.log("Server UTC ISO:", isoString);

    setServerTime(isoString);
  };

  useEffect(() => {
    EventBus.emit("updateProfile");
    getCompanyDetails();
  }, []);

  const uploadCompanyLogo = async (e) => {
    if (!e) return;
    setCompanyLogoUploading(true);
    const formData = new FormData();
    formData.append("image", e.target.files[0]);

    try {
      const response = await axios.post(UPLOAD_COMPANY_LOGO, formData, {
        headers: generateURLOptions("multipart/form-data"),
      });
      console.log("UPLOAD RESPONSE: ", JSON.stringify(response.data));
      if (response?.data?.success === false) {
        alert(response?.data?.message ?? "");
        return;
      }

      setCompanyLogo(response.data.data?.file);
      setCompanyLogoUploading(false);
    } catch (error) {
      console.error("Organiation Icon Error: ", error);
      return alert(error);
    }
  };

  const getCompanyDetails = async () => {
    try {
      const response = await axios.get(GET_COMPANY_DETAILS, {
        headers: generateURLOptions(),
      });

      console.log("COMPANY DETAILS: ", response.data.data);
      if (response?.data?.success === false) {
        alert(response?.data?.message ?? "");
        return;
      }

      setCompany(response.data.data);
    } catch (error) {
      console.error("Organiation Icon Error: ", error);
      return alert(error);
    }
  };

  const updateCompanyDetails = async () => {
    try {
      const response = await axios.post(
        UPDATE_COMPANY_DETAILS,
        {
          name: companyName,
          phone: companyPhone,
          logo: companyLogo,
          reminderTime: serverTime,
          colour: companyColour,
        },
        {
          headers: generateURLOptions(),
        },
      );
      console.log("UPLOAD RESPONSE: ", JSON.stringify(response.data));
      if (response?.data?.success === false) {
        alert(response?.data?.message ?? "");
        return;
      }

      EventBus.emit("updateProfile");

      // Show success dialog
      setShowSuccessDialog(true);
    } catch (error) {
      console.error("Organiation Icon Error: ", error);
      return alert(error);
    }
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
  };

  return (
    <>
      <div className="relative">
        <div
          className="z-0 absolute top-0 bottom-0 left-0 right-0"
          style={{
            background: `linear-gradient(45deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.8) 45%, ${
              profile?.company_id
                ? profile?.company_id?.company_primary_color
                : "rgba(81, 83, 245, 1)"
            } 150%)`,
          }}
        />

        <FadeIn className="overflow-hidden" delay={250}>
          <div className="relative z-[20] pt-12 w-full md:w-2/3 mx-auto px-6 md:px-0 pb-12">
            {/* Priority Notifications */}
            <div className="flex flex-row justify-center items-center">
              <h4 className="font-medium text-xl mb-2">
                Organisation Configurator
              </h4>

              <div style={{ flex: 1 }} />

              <div className="w-1/4">
                <FilledButton text={"Save"} onPress={updateCompanyDetails} />
              </div>
            </div>

            {/* Company Details */}
            <h4 className="font-medium text-xl mt-4 mb-2">
              Organisation Information
            </h4>

            {/* Organisation Logo */}
            <div className="flex flex-col md:flex-row justify-center items-center">
              <div className="flex flex-col w-full md:w-2/5 mt-2">
                <p className="text-base font-normal mb-1">Company Logo</p>
                <p className="text-sm font-normal mb-1">
                  We recommend uploading a logo with a transparent background
                  and contrasting colours.
                </p>

                <p className="text-sm text-mpPurple font-normal mb-1">
                  Recommended Dimensions are: 560px W x 456px H
                </p>
              </div>

              <div style={{ flex: 1 }} />

              <img
                className="w-20 h-16 object-contain mr-0 md:mr-8"
                src={companyLogo ?? userPlaceholder}
              />

              <div className="w-full md:w-1/5">
                <div className="relative w-full">
                  {!companyLogoUploading ? (
                    <FilledButton
                      text={"Update Logo"}
                      onPress={() =>
                        document.getElementById("fileUpload").click()
                      }
                    />
                  ) : (
                    <>
                      <div
                        className="h-10 w-10 rounded-full border-4 border-mpPurple/30 border-t-mpPurple animate-spin"
                        style={{ marginTop: 12, marginBottom: 12 }}
                      />
                    </>
                  )}
                  <input
                    id="fileUpload"
                    type="file"
                    onChange={uploadCompanyLogo}
                    accept="image/*"
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row mt-4">
              <div className="md:w-[49%]">
                <CommonTextField
                  title={"Company Name"}
                  value={companyName}
                  onChange={setCompanyName}
                />
              </div>

              <div style={{ flex: 1 }} />

              <div className="md:w-[49%]">
                <CommonTextField
                  title={"Company Phone"}
                  value={companyPhone}
                  onChange={setCompanyPhone}
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row mt-4">
              <div className="md:w-[49%]">
                <p className="text-base font-normal mb-1">
                  Company Reminder Time
                </p>
                <p className="text-sm font-normal mb-2">
                  This is the time each day that users will be reminded to
                  check-in.
                </p>
                <div className="flex flex-row items-center w-full rounded-lg mb-2 border border-[#5153F5] bg-white px-2 py-1">
                  <TimePicker
                    start="00:00"
                    end="23:59"
                    step={15}
                    onChange={handleTimeChange}
                    value={selectedTime}
                    className="w-full py-2 outline-none bg-transparent text-black" // optional, might help on some versions
                  />
                </div>
              </div>

              <div style={{ flex: 1 }} />

              <div className="md:w-[49%]">
                <p className="text-base font-normal mb-1">Organisation Code</p>
                <p className="text-sm font-normal mb-2">
                  Your Organisation Code is used to invite users into Mind Pulse
                  to join your organisation.
                </p>
                <p className="text-2xl md:text-xl py-2 font-semibold text-black">
                  {profile?.manager_code ?? ""}
                </p>
              </div>
            </div>

            <div className="mt-4 md:mt-0">
              <h4 className="text-black font-normal mt-2 mb-0">
                Company Primary Colour
              </h4>
              <p className="text-sm font-normal mb-2">
                This primary colour is used as the background for your
                organisation, this will become visible on each users device.
              </p>
              <HexColorPicker
                style={{ width: "100%" }}
                color={companyColour}
                onChange={setCompanyColour}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-medium text-xl mt-4 mb-1">
                  Subscription & Seat Management
                </h4>

                <p className="mt-1 font-normal text-black text-base">
                  You'll find your organsiations subscription & user access
                  details below.
                </p>

                <div className="mt-4 border border-slate-300 rounded-lg flex flex-col justify-center items-center py-4">
                  <p className="mb-0 font-semibold text-black text-lg">
                    {company?.companyPlan?.name ?? "Mind Pulse Heart"}
                  </p>
                  <p className="mb-0 font-medium text-center text-black text-base">
                    {profile?.company_id?.seats ?? 0} Seats
                  </p>
                  <p className="mb-0 font-medium text-center text-black text-base">
                    Using {profile?.company_id?.usedSeats ?? 0} Seats
                  </p>
                  <p className="mb-0 font-medium text-center text-black text-base">
                    $
                    {parseFloat(
                      company?.perUserPrice ??
                        company?.companyPlan?.price ??
                        0.0,
                    ).toFixed(2)}{" "}
                    AUD p/Seat p/Month
                  </p>

                  <p className="mb-0 font-medium text-center text-green-600 text-base">
                    Renews {profile?.company_id?.planFrequency ?? "monthly"}
                  </p>

                  <p className="mb-0 font-normal text-center text-black text-sm mt-2">
                    Please contact our Admin Team to edit your Subscription
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-xl mt-4 mb-1">
                  Support Connect Providers
                </h4>

                <p className="mt-1 font-normal text-black text-base">
                  You'll find your organsiations subscription & user access
                  details below.
                </p>

                <p className="font-normal text-black text-base text-center mt-12">
                  No Company-Specific Support Connect Providers have been added
                  yet
                </p>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      {/* Success Dialog */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] transition-opacity duration-300 ${
          showSuccessDialog ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className={`bg-white rounded-xl w-full max-w-md mx-4 p-6 shadow-xl transition-all duration-300 ${
            showSuccessDialog ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
        >
          {/* Close Button */}
          <button
            onClick={handleCloseSuccessDialog}
            className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <IoClose size={24} />
          </button>

          {/* Success Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <IoCheckmarkCircle size={40} className="text-green-600" />
            </div>
          </div>

          {/* Success Title */}
          <h3 className="text-xl font-semibold text-center text-gray-900 mb-3">
            Successfully Saved
          </h3>

          {/* Success Message */}
          <p className="text-center text-gray-600 mb-6">
            Your changes have been saved. It may take a few minutes before your
            changes are visible across the Mind Pulse Platform.
          </p>

          {/* OK Button */}
          <button
            onClick={handleCloseSuccessDialog}
            className="w-full px-4 py-3 bg-green-600 rounded-lg text-white font-medium hover:bg-green-700 transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </>
  );
};

export default CompanyConfigurator;
